export type BlockType =
  | 'title'
  | 'text'
  | 'image'
  | 'video'
  | 'button'
  | 'dynamic'
  | 'logo'
  | 'social'
  | 'html'
  | 'divider'
  | 'product'
  | 'nav'
  | 'spacer';

export type TextAlign = 'left' | 'center' | 'right' | 'justify';

export type BaseBlockLayout = {
  id: string;
  type: BlockType;
  align: TextAlign;
  width: number;
  paddingTop: number;
  paddingRight: number;
  paddingBottom: number;
  paddingLeft: number;
  marginTop: number;
  marginRight: number;
  marginBottom: number;
  marginLeft: number;
  bg: string;
  bgImage: string;
};

export type TitleBlock = BaseBlockLayout & {
  type: 'title';
  text: string;
  size: number;
  color: string;
  font: string;
  weight: number;
  lineHeight: number;
};

export type TextBlock = BaseBlockLayout & {
  type: 'text';
  text: string;
  size: number;
  color: string;
  font: string;
  weight: number;
  lineHeight: number;
};

export type ImageBlock = BaseBlockLayout & {
  type: 'image';
  src: string;
  placeholder: string;
  height: number;
};

export type VideoBlock = BaseBlockLayout & {
  type: 'video';
  thumb: string;
  label: string;
  height: number;
};

export type ButtonBlock = BaseBlockLayout & {
  type: 'button';
  text: string;
  href: string;
  btnBg: string;
  btnColor: string;
  radius: number;
  size: number;
};

export type DynamicBlock = BaseBlockLayout & {
  type: 'dynamic';
  source: string;
  fallback: string;
};

export type LogoBlock = BaseBlockLayout & {
  type: 'logo';
  text: string;
  size: number;
  color: string;
  font: string;
  weight: number;
};

export type SocialLink = {
  network: string;
  url: string;
};

export type SocialBlock = BaseBlockLayout & {
  type: 'social';
  links: SocialLink[];
};

export type HtmlBlock = BaseBlockLayout & {
  type: 'html';
  code: string;
};

export type DividerBlock = BaseBlockLayout & {
  type: 'divider';
  color: string;
  thickness: number;
};

export type ProductBlock = BaseBlockLayout & {
  type: 'product';
  name: string;
  price: string;
  img: string;
};

export type NavBlock = BaseBlockLayout & {
  type: 'nav';
  items: string[];
  color: string;
};

export type SpacerBlock = BaseBlockLayout & {
  type: 'spacer';
  height: number;
};

export type EmailBlock =
  | TitleBlock
  | TextBlock
  | ImageBlock
  | VideoBlock
  | ButtonBlock
  | DynamicBlock
  | LogoBlock
  | SocialBlock
  | HtmlBlock
  | DividerBlock
  | ProductBlock
  | NavBlock
  | SpacerBlock;

export type EditorTab = 'content' | 'style';
export type DeviceMode = 'desktop' | 'mobile';

export type EmailGlobalStyles = {
  backgroundColor: string;
  contentBackground: string;
  fontFamily: string;
  baseSize: number;
  defaultTextColor: string;
  linkColor: string;
  emailWidth: number;
  borderRadius: number;
};

export const DEFAULT_GLOBAL_STYLES: EmailGlobalStyles = {
  backgroundColor: '#F4F5F7',
  contentBackground: '#FFFFFF',
  fontFamily: 'Arial',
  baseSize: 14,
  defaultTextColor: '#0F172A',
  linkColor: '#1530C7',
  emailWidth: 600,
  borderRadius: 0,
};

export type HtmlEditorProps = {
  id?: string;
  templateName?: string;
  value?: string;
  onChange?: (html: string) => void;
  onTemplateNameChange?: (name: string) => void;
  onClose?: () => void;
  onSave?: (payload: { name: string; html: string }) => void;
  onPreview?: (html: string) => void;
  disabled?: boolean;
};

export type BlockPatch = Partial<EmailBlock>;
